import { ProposalContent, ProposalImages } from "@/types/proposal";
import { SectionHeader } from "./SectionHeader";
import { SignatureSection } from "./SignatureSection";
import foundryLogo from "@/assets/foundry-logo.png";

interface ProposalPage3Props {
  content: ProposalContent;
  images: ProposalImages;
  proposalName?: string;
  isClientMode?: boolean;
  creatorUserId?: string;
}

export const ProposalPage3 = ({ content, images, proposalName = "Untitled", isClientMode = false, creatorUserId }: ProposalPage3Props) => {
  const colors = ["bg-amber-500", "bg-slate-400", "bg-slate-500", "bg-slate-600", "bg-slate-700"];
  
  return (
    <div className="proposal-page avoid-break w-full mx-auto bg-white shadow-xl mb-8 print:shadow-none print:rounded-none print:mb-0 p-8 print:p-4">
      <div className="avoid-break">
        <SectionHeader 
          title="Timeline & Next Steps" 
          right={<div className="text-sm text-slate-500">PROJECT {content.projectNumber} | PAGE 03</div>} 
        />
      </div>

      <div className="flex-1 flex flex-col">
        <div className="grid grid-cols-2 gap-12 mb-12 avoid-break">
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-slate-800 tracking-tight border-b border-slate-200 pb-3">
              PROJECT TIMELINE
            </h3>
            <div className="space-y-6">
              {content.timeline.map((item, index) => {
                const color = colors[index % colors.length];
                return (
                  <div key={index} className="flex items-start space-x-4 avoid-break">
                    <div className={`w-4 h-4 ${color} flex-shrink-0 mt-1`}></div>
                    <div className="flex-1">
                      <div className="font-bold text-slate-800 tracking-wide text-sm uppercase">
                        {item.period}
                      </div>
                      <div className="text-slate-600 text-base mt-1">{item.task}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-lg font-medium text-slate-800 tracking-tight border-b border-slate-200 pb-3">
              NEXT STEPS
            </h3>
            <div className="space-y-4">
              {content.nextSteps.map((step, index) => (
                <div key={index} className="flex items-start space-x-3 avoid-break">
                  <div className="w-6 h-6 border-2 border-slate-800 flex-shrink-0 mt-0.5 flex items-center justify-center">
                    <span className="text-slate-800 font-bold text-sm">{index + 1}</span>
                  </div>
                  <span className="text-slate-700 text-base leading-relaxed">{step}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="avoid-break bg-slate-800 text-white p-8 text-center shadow-lg mb-6">
          <p className="text-base leading-relaxed">{content.closingNote}</p>
        </div>

        <div className="avoid-break">
          <SignatureSection 
            proposalName={proposalName}
            content={content}
            images={images}
            isClientMode={isClientMode}
            creatorUserId={creatorUserId}
          />
        </div>

        <div className="text-center mt-auto mb-6 avoid-break">
          <div className="mb-3">
            <img 
              src={images.logo || foundryLogo} 
              alt="Foundry Cabinet Co. Logo" 
              className="max-w-24 max-h-10 mx-auto object-contain" 
            />
          </div>
          <div className="text-slate-600 text-xs space-y-1">
            <div>{content.companyAddress}</div>
            <div>{content.companyPhone} | {content.companyEmail}</div>
            <div>{content.companyWebsite}</div>
          </div>
          <div className="text-slate-500 text-xs tracking-widest uppercase mt-2">{content.tagline}</div>
        </div>
      </div>
    </div>
  );
};
